<script setup>
import { useForm } from "@/form";
import { BanIcon, DownloadIcon, LoaderCircleIcon, RefreshCwIcon, SaveIcon } from "lucide-vue-next";
import { computed, inject, onMounted, ref, useSlots } from "vue";
import { useI18n } from "vue-i18n";
import FormModal from "@Components/Modal/FormModal.vue";
import PostCombobox from "@Pages/ImageGenerator/Partials/PostCombobox.vue";
import { FormInput } from "@Components/Form";
import { ArrowPathIcon } from "@heroicons/vue/20/solid";
import ActionMessage from "@Components/ActionMessage.vue";
import { PrimaryButton } from "@Components/Button";
import { Modal } from "@Components/Modal";

const slots = useSlots();

const prompt = computed(() => (slots.default()?.[0]?.children ?? "").trim());

const props = defineProps({
    messageIndex: Number,
    toolIndex: Number,
});

const { t: __ } = useI18n();

const loadedTools = inject("loadedTools");
const form = inject("form");

const toolId = computed(() => props.messageIndex + "-" + props.toolIndex);
const processing = ref(false);
const result = ref(null);

const downloadImage = () => {
    if (result.value.image?.base64) {
        var a = document.createElement("a");
        a.href = "data:image/png;base64," + result.value.image.base64;
        a.download = "image.png";
        a.click();
    }
};

const generateImage = async () => {
    processing.value = true;

    const response = await useForm({
        prompt: prompt.value,
        image_model: form.models.image,
    }).post("image_generator_generate");

    if (response.image !== undefined) {
        result.value = response;
    }

    processing.value = false;
};

const saveModal = ref();
const promptModal = ref();
const generateImageDescriptionForm = useForm({
    title: "",
});

const saveImageForm = useForm({
    name: "",
    description: "",
    image_model: "dall-e-3",
    path: undefined,
    post_id: null,
});

const generateImageDescription = async (title) => {
    if (generateImageDescriptionForm.processing) {
        return;
    }

    generateImageDescriptionForm.title = title;

    const result = await generateImageDescriptionForm.post("image_generator_generate_description");

    if (result.description !== undefined) {
        saveImageForm.description = result.description;
    }
};

const submitSaveImage = async () => {
    if (!result.value) {
        return;
    }

    if (saveImageForm.processing) {
        return;
    }

    saveImageForm.image_model = result.value.image_model;
    saveImageForm.path = result.value.path;

    await saveImageForm.post("image_generator_save");

    saveImageForm.reset();
};

const updateSelectedPost = (post) => {
    if (post) {
        saveImageForm.post_id = post.ID;
        saveImageForm.name = post.post_title;
        generateImageDescription(post.post_title);
    }
};

onMounted(() => {
    if (loadedTools.includes(toolId.value)) {
        return;
    }

    loadedTools.push(props.messageIndex + "-" + props.toolIndex);

    generateImage();
});
</script>
<template>
    <div class="aspect-video overflow-hidden relative block bg-gray-100 rounded-md group dark:bg-gray-900">
        <div v-if="processing" class="w-full h-full absolute flex flex-col justify-center items-center text-gray-900 dark:text-white">
            <LoaderCircleIcon class="size-6 animate-spin" />
            <span class="mt-3 text-sm">{{ __("Image en cours de génération...") }}</span>
        </div>
        <template v-else-if="result">
            <img class="w-full h-full object-cover absolute" :src="'data:image/png;base64, ' + result.image.base64" />
            <div class="absolute top-4 right-4 hidden items-center space-x-3 group-hover:flex">
                <button @click="generateImage" class="bg-opacity-50 flex justify-center items-center p-2 bg-gray-900 rounded-md hover:bg-opacity-75">
                    <RefreshCwIcon class="size-4 text-white" />
                </button>
                <button
                    @click="$refs['saveModal'].show()"
                    class="bg-opacity-50 flex justify-center items-center p-2 bg-gray-900 rounded-md hover:bg-opacity-75">
                    <SaveIcon class="size-4 text-white" />
                </button>
                <button @click="downloadImage" class="bg-opacity-50 flex justify-center items-center p-2 bg-gray-900 rounded-md hover:bg-opacity-75">
                    <DownloadIcon class="size-4 text-white" />
                </button>
                <FormModal ref="saveModal" @submit="submitSaveImage" :title="__('Sauvegarder l\'image')">
                    <template #body>
                        <div class="space-y-6">
                            <PostCombobox :modelValue="saveImageForm.post_id" @update:modelValue="updateSelectedPost" :postTypes="[]" />
                            <FormInput v-model="saveImageForm.name" :label="__('Titre')" :z="1" />
                            <FormInput
                                v-model="saveImageForm.description"
                                :label="__('Texte alternatif')"
                                :class="[saveImageForm.name.length > 0 ? 'pr-12' : '']"
                                :z="1">
                                <template #action v-if="saveImageForm.name.length > 0">
                                    <div class="absolute inset-y-0 right-0 flex items-center mr-4">
                                        <svg
                                            v-if="generateImageDescriptionForm.processing"
                                            class="w-4 h-4 text-gray-200 animate-spin"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                            <path
                                                class="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <ArrowPathIcon
                                            v-else
                                            class="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-200"
                                            @click="generateImageDescription(saveImageForm.name)" />
                                    </div>
                                </template>
                            </FormInput>
                        </div>
                    </template>
                    <template #actions>
                        <div class="flex justify-end items-center">
                            <ActionMessage :on="saveImageForm.recentlySuccessful" class="mr-3"> {{ __("Sauvegardé.") }} </ActionMessage>
                            <PrimaryButton :processing="saveImageForm.processing" type="submit" :z="1">
                                {{ __("Sauvegarder") }}
                            </PrimaryButton>
                        </div>
                    </template>
                </FormModal>
                <Modal ref="promptModal" :title="__('Prompt de génération')">
                    <div class="p-4 text-sm text-gray-900 dark:text-white">{{ prompt }}</div>
                </Modal>
            </div>
            <div class="absolute right-4 bottom-4 hidden items-center space-x-3 group-hover:flex">
                <button
                    @click="$refs['promptModal'].show()"
                    class="bg-opacity-50 flex justify-center items-center p-2 text-gray-900 bg-gray-900 rounded-md dark:text-white hover:bg-opacity-75">
                    {{ __("Prompt") }}
                </button>
            </div>
        </template>
        <div v-else class="w-full h-full absolute flex flex-col justify-center items-center text-gray-900 dark:text-white">
            <BanIcon class="size-6" />
            <span class="mt-3 text-sm">{{ __("Aucune image générée") }}</span>
            <PrimaryButton :z="1" @click="generateImage" class="mt-3">{{ __("Réessayer") }}</PrimaryButton>
        </div>
    </div>
</template>
